import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, ImageIcon, Grid3X3, Palette, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import LogoStorageService from '../../../services/logoStorageService';

const QRStylingPanel = ({ styling, onStylingChange }) => {
  const [activeTab, setActiveTab] = useState('colors');
  const fileInputRef = useRef(null);
  const [logoError, setLogoError] = useState(null);
  const [logoSuccess, setLogoSuccess] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [userLogos, setUserLogos] = useState([]);
  const [loadingUserLogos, setLoadingUserLogos] = useState(false);
  
  const { user } = useAuth();

  // Load user logos when logo tab is opened - FIXED: Moved to top level
  useEffect(() => {
    if (activeTab === 'logo' && user && userLogos?.length === 0) {
      loadUserLogos();
    }
  }, [activeTab, user]);

  // Load user's existing logos when logo tab is opened
  const loadUserLogos = async () => {
    if (!user || loadingUserLogos) return;
    
    setLoadingUserLogos(true);
    try {
      const result = await LogoStorageService?.getUserLogos(user?.id);
      if (result?.success) {
        setUserLogos(result?.logos);
      }
    } catch (error) {
      console.log('Error loading user logos:', error);
    } finally {
      setLoadingUserLogos(false);
    }
  };

  const handleColorChange = (colorType, value) => {
    onStylingChange({
      ...styling,
      [colorType]: value
    });
  };

  const handleLogoUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || !user) return;

    setLogoLoading(true);
    setLogoError(null);
    setLogoSuccess(null);

    try {
      // Optimize image first
      const optimizationResult = await LogoStorageService?.optimizeImageForQR(file);
      if (!optimizationResult?.success) {
        setLogoError(optimizationResult?.error);
        return;
      }

      // Upload optimized image to Supabase Storage
      const uploadResult = await LogoStorageService?.uploadLogo(optimizationResult?.optimizedFile, user?.id);
      if (!uploadResult?.success) {
        setLogoError(uploadResult?.error);
        return;
      }

      // Extract suggested text from filename (remove extension and common words)
      const fileName = uploadResult?.fileName || '';
      const suggestedText = fileName
        ?.replace(/\.[^/.]+$/, '') // Remove file extension
        ?.replace(/[-_]/g, ' ') // Replace dashes and underscores with spaces
        ?.replace(/\b(logo|icon|image|pic|photo)\b/gi, '') // Remove common words
        ?.trim()
        ?.toUpperCase() || '';

      // Update styling with new logo URL and suggested text
      onStylingChange({
        ...styling,
        logoUrl: uploadResult?.publicUrl,
        logoFileName: uploadResult?.fileName,
        logoFilePath: uploadResult?.filePath,
        logoText: suggestedText || styling?.logoText || '', // Use suggested text if available
        logoStyle: styling?.logoStyle || 'embedded' // Default to embedded style
      });

      setLogoSuccess('Logo uploaded successfully!');
      setTimeout(() => setLogoSuccess(null), 3000);
      
      // Reload user logos
      loadUserLogos();
    } catch (error) {
      setLogoError('Failed to upload logo. Please try again.');
    } finally {
      setLogoLoading(false);
      // Reset file input
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogoUrlSubmit = async () => {
    if (!urlInput?.trim()) return;

    setLogoLoading(true);
    setLogoError(null);
    setLogoSuccess(null);

    try {
      // Validate URL first
      const validation = await LogoStorageService?.validateLogoUrl(urlInput);
      if (!validation?.success) {
        setLogoError(validation?.error);
        return;
      }

      // Create proxy URL to avoid CORS issues
      const proxyUrl = LogoStorageService?.createProxyUrl(urlInput);
      
      onStylingChange({
        ...styling,
        logoUrl: proxyUrl,
        logoFileName: 'External URL',
        logoFilePath: null
      });

      setLogoSuccess('External logo loaded successfully!');
      setTimeout(() => setLogoSuccess(null), 3000);
      setUrlInput('');
    } catch (error) {
      setLogoError('Failed to load logo from URL. Please check the URL and try again.');
    } finally {
      setLogoLoading(false);
    }
  };

  const handleRemoveLogo = async () => {
    // Delete from storage if it's an uploaded file
    if (styling?.logoFilePath && user) {
      try {
        await LogoStorageService?.deleteLogo(styling?.logoFilePath);
        loadUserLogos(); // Refresh user logos
      } catch (error) {
        console.log('Error deleting logo:', error);
      }
    }
    
    onStylingChange({
      ...styling,
      logoUrl: null,
      logoFileName: null,
      logoFilePath: null
    });
    setLogoError(null);
    setLogoSuccess(null);
  };

  const handleSelectExistingLogo = (logo) => {
    onStylingChange({
      ...styling,
      logoUrl: logo?.publicUrl,
      logoFileName: logo?.name,
      logoFilePath: logo?.filePath
    });
    setLogoSuccess('Logo selected!');
    setTimeout(() => setLogoSuccess(null), 2000);
  };

  const tabs = [
    { id: 'colors', label: 'Colors', icon: Palette },
    { id: 'logo', label: 'Logo', icon: ImageIcon },
    { id: 'advanced', label: 'Pattern', icon: Grid3X3 }
  ];

  const renderColorsTab = () => (
    <div className="space-y-4">
      {/* Foreground Color */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Foreground Color
        </label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={styling?.foregroundColor || '#000000'}
            onChange={(e) => handleColorChange('foregroundColor', e?.target?.value)}
            className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
          />
          <input
            type="text"
            value={styling?.foregroundColor || '#000000'}
            onChange={(e) => handleColorChange('foregroundColor', e?.target?.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="#000000"
          />
        </div>
      </div>

      {/* Background Color */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Background Color
        </label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={styling?.backgroundColor || '#ffffff'}
            onChange={(e) => handleColorChange('backgroundColor', e?.target?.value)}
            className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
          />
          <input
            type="text"
            value={styling?.backgroundColor || '#ffffff'}
            onChange={(e) => handleColorChange('backgroundColor', e?.target?.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="#ffffff"
          />
        </div>
      </div>

      {/* QR Code Size */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          QR Code Size: {styling?.size || 256}px
        </label>
        <input
          type="range"
          min="128"
          max="512"
          step="32"
          value={styling?.size || 256}
          onChange={(e) => handleColorChange('size', parseInt(e?.target?.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>128px</span>
          <span>512px</span>
        </div>
      </div>

      {/* Rounded Corners */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="roundedCorners"
            checked={styling?.roundedCorners || false}
            onChange={(e) => handleColorChange('roundedCorners', e?.target?.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="roundedCorners" className="text-sm text-gray-700">
            Rounded QR code blocks
          </label>
        </div>
        <p className="text-xs text-gray-500 ml-6">
          Makes QR code blocks appear rounded instead of square
        </p>
      </div>

      {/* Error Correction Level */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Error Correction Level
        </label>
        <select
          value={styling?.errorCorrectionLevel || 'M'}
          onChange={(e) => handleColorChange('errorCorrectionLevel', e?.target?.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="L">Low (7%)</option>
          <option value="M">Medium (15%)</option>
          <option value="Q">Quartile (25%)</option>
          <option value="H">High (30%)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Higher correction allows better logo integration
        </p>
      </div>
    </div>
  );

  const renderLogoTab = () => {
    return (
      <div className="space-y-6">
        {/* Status Messages */}
        {logoError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {logoError}
          </div>
        )}
        {logoSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            {logoSuccess}
          </div>
        )}
        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Upload New Logo
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
              disabled={logoLoading}
            />
            <button
              type="button"
              onClick={() => fileInputRef?.current?.click()}
              disabled={logoLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {logoLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {logoLoading ? 'Uploading...' : 'Choose Logo File'}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              PNG, JPG, WEBP, or SVG (Max 5MB)
            </p>
          </div>
        </div>
        {/* External Logo URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Or use external logo URL
          </label>
          <div className="flex gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e?.target?.value)}
              placeholder="https://example.com/logo.png"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={logoLoading}
            />
            <button
              type="button"
              onClick={handleLogoUrlSubmit}
              disabled={logoLoading || !urlInput?.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {logoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load'}
            </button>
          </div>
        </div>
        {/* Current Logo Display */}
        {styling?.logoUrl && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Current Logo</span>
              <button
                type="button"
                onClick={handleRemoveLogo}
                className="text-red-500 hover:text-red-700"
                disabled={logoLoading}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <img
                src={styling?.logoUrl}
                alt="Logo preview"
                className="w-16 h-16 object-contain border border-gray-300 rounded bg-white"
                onError={() => setLogoError('Failed to load logo')}
              />
              <div className="text-sm text-gray-600">
                {styling?.logoFileName || 'External URL'}
              </div>
            </div>
          </div>
        )}
        {/* Logo Integration Type */}
        {styling?.logoUrl && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Logo Integration Style
            </label>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="logoEmbedded"
                  name="logoStyle"
                  value="embedded"
                  checked={styling?.logoStyle !== 'text'}
                  onChange={() => handleColorChange('logoStyle', 'embedded')}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="logoEmbedded" className="text-sm text-gray-700">
                  Embedded Image (Traditional)
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="logoText"
                  name="logoStyle"
                  value="text"
                  checked={styling?.logoStyle === 'text'}
                  onChange={() => handleColorChange('logoStyle', 'text')}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="logoText" className="text-sm text-gray-700">
                  Text Integration (Like "TAYLOR" example)
                </label>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Text integration makes your logo more prominent and readable like the "TAYLOR" example
            </p>
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700">
                <strong>💡 Pro Tip:</strong> Upload a logo file, then switch to "Text Integration" and enter your brand name or text. 
                This creates a prominent, readable text overlay that stands out in your QR code.
              </p>
            </div>
          </div>
        )}

        {/* Text Integration Options */}
        {styling?.logoUrl && styling?.logoStyle === 'text' && (
          <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800">Text Integration Settings</h4>
            
            {/* Logo Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Embed Text in QR
              </label>
              <input
                type="text"
                value={styling?.logoText || ''}
                onChange={(e) => handleColorChange('logoText', e?.target?.value)}
                placeholder="e.g., TAYLOR, BRAND, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength="10"
              />
              <p className="text-xs text-gray-500 mt-1">
                Text will be integrated into QR pattern (like your example)
              </p>
            </div>

            {/* Text Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text Size: {styling?.logoTextSize || 24}px
              </label>
              <input
                type="range"
                min="12"
                max="72"
                step="2"
                value={styling?.logoTextSize || 24}
                onChange={(e) => handleColorChange('logoTextSize', parseInt(e?.target?.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>12px</span>
                <span>72px</span>
              </div>
            </div>

            {/* Text Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={styling?.logoTextColor || styling?.foregroundColor || '#000000'}
                  onChange={(e) => handleColorChange('logoTextColor', e?.target?.value)}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={styling?.logoTextColor || styling?.foregroundColor || '#000000'}
                  onChange={(e) => handleColorChange('logoTextColor', e?.target?.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="#000000"
                />
              </div>
            </div>

            {/* Text Font */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Font Family
              </label>
              <select
                value={styling?.logoTextFont || 'Arial'}
                onChange={(e) => handleColorChange('logoTextFont', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Arial">Arial</option>
                <option value="Helvetica">Helvetica</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Georgia">Georgia</option>
                <option value="Verdana">Verdana</option>
                <option value="Courier New">Courier New</option>
                <option value="Impact">Impact</option>
                <option value="Trebuchet MS">Trebuchet MS</option>
              </select>
            </div>

            {/* Text Weight */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Font Weight
              </label>
              <select
                value={styling?.logoTextWeight || 'bold'}
                onChange={(e) => handleColorChange('logoTextWeight', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
                <option value="bolder">Bolder</option>
                <option value="lighter">Lighter</option>
              </select>
            </div>
          </div>
        )}

        {/* Embedding Style - Only when logo is present */}
        {styling?.logoUrl && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Embedding Style</h4>
            <div>
              <label className="block text-sm text-gray-700 mb-2">
                How should your logo be embedded?
              </label>
              <select
                value={styling?.embeddingStyle || 'overlay'}
                onChange={(e) => handleColorChange('embeddingStyle', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="overlay">Standard Overlay</option>
                <option value="embedded">Embedded in Pattern</option>
                <option value="integrated">Fully Integrated</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {styling?.embeddingStyle === 'integrated' ? 'Logo becomes part of QR pattern like TAYLOR example' 
                  : styling?.embeddingStyle === 'embedded' ? 'Logo replaces QR dots in center area' : 'Logo sits on top of QR code'}
              </p>
            </div>
          </div>
        )}

        {/* Logo Size Slider - Only for embedded style */}
        {styling?.logoUrl && styling?.logoStyle !== 'text' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Logo Size: {styling?.logoSize || 20}%
            </label>
            <input
              type="range"
              min="10"
              max="80"
              step="5"
              value={styling?.logoSize || 20}
              onChange={(e) => handleColorChange('logoSize', parseInt(e?.target?.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>10%</span>
              <span>80%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Larger logos may affect QR code readability
            </p>
          </div>
        )}

        {/* Advanced Logo Options */}
        {styling?.logoUrl && (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Advanced Logo Options</h4>
            
            {/* Logo Opacity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logo Opacity: {Math.round((styling?.logoOpacity || 1) * 100)}%
              </label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.1"
                value={styling?.logoOpacity || 1}
                onChange={(e) => handleColorChange('logoOpacity', parseFloat(e?.target?.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>10%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Blend Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Blend Mode
              </label>
              <select
                value={styling?.logoBlendMode || 'normal'}
                onChange={(e) => handleColorChange('logoBlendMode', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="normal">Normal</option>
                <option value="multiply">Multiply</option>
                <option value="screen">Screen</option>
                <option value="overlay">Overlay</option>
                <option value="soft-light">Soft Light</option>
                <option value="hard-light">Hard Light</option>
                <option value="color-dodge">Color Dodge</option>
                <option value="color-burn">Color Burn</option>
              </select>
            </div>

            {/* Hide Background Dots */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hideBackgroundDots"
                checked={styling?.hideBackgroundDots !== false}
                onChange={(e) => handleColorChange('hideBackgroundDots', e?.target?.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="hideBackgroundDots" className="text-sm text-gray-700">
                Hide background dots behind logo
              </label>
            </div>
          </div>
        )}
        {/* Previously Uploaded Logos */}
        {user && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Your Uploaded Logos
              </label>
              <button
                type="button"
                onClick={loadUserLogos}
                disabled={loadingUserLogos}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                {loadingUserLogos ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
              </button>
            </div>
            
            {loadingUserLogos ? (
              <div className="text-center py-4">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              </div>
            ) : userLogos?.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No uploaded logos yet
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {userLogos?.map((logo, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelectExistingLogo(logo)}
                    className="relative group border border-gray-300 rounded-lg p-2 hover:border-blue-500 transition-colors"
                  >
                    <img
                      src={logo?.publicUrl}
                      alt={logo?.name}
                      className="w-full h-16 object-contain bg-white rounded"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-opacity" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderAdvancedTab = () => {
    return (
    <div className="space-y-4">
      {/* QR Pattern Style */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">QR Pattern Style</h4>
        
        <div>
          <label className="block text-xs text-gray-700 mb-2">Dot Style</label>
          <select
            value={styling?.dotStyle || 'square'}
            onChange={(e) => handleColorChange('dotStyle', e?.target?.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="square">Square</option>
            <option value="rounded">Rounded</option>
            <option value="dots">Dots</option>
            <option value="classy">Classy</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-700 mb-2">Corner Style</label>
          <select
            value={styling?.cornerStyle || 'square'}
            onChange={(e) => handleColorChange('cornerStyle', e?.target?.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="square">Square</option>
            <option value="extra-rounded">Extra Rounded</option>
            <option value="dot">Dot</option>
          </select>
        </div>
      </div>

      {/* Logo Margin */}
      {styling?.logoUrl && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo Margin: {styling?.logoMargin || 8}px
          </label>
          <input
            type="range"
            min="0"
            max="20"
            step="2"
            value={styling?.logoMargin || 8}
            onChange={(e) => handleColorChange('logoMargin', parseInt(e?.target?.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>0px</span>
            <span>20px</span>
          </div>
        </div>
      )}

      {/* Advanced Options */}
      {styling?.logoUrl && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="hideBackgroundDots"
              checked={styling?.hideBackgroundDots !== false}
              onChange={(e) => handleColorChange('hideBackgroundDots', e?.target?.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="hideBackgroundDots" className="text-sm text-gray-700">
              Hide dots behind logo
            </label>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="excavate"
              checked={styling?.excavate !== false}
              onChange={(e) => handleColorChange('excavate', e?.target?.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="excavate" className="text-sm text-gray-700">
              Excavate background (recommended)
            </label>
          </div>

          {/* Logo Border Radius */}
          <div>
            <label className="block text-xs text-gray-700 mb-1">
              Logo Border Radius: {styling?.logoBorderRadius || 0}px
            </label>
            <input
              type="range"
              min="0"
              max="20"
              step="2"
              value={styling?.logoBorderRadius || 0}
              onChange={(e) => handleColorChange('logoBorderRadius', parseInt(e?.target?.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Logo Opacity */}
          <div>
            <label className="block text-xs text-gray-700 mb-1">
              Logo Opacity: {Math.round((styling?.logoOpacity || 1.0) * 100)}%
            </label>
            <input
              type="range"
              min="0.1"
              max="1.0"
              step="0.1"
              value={styling?.logoOpacity || 1.0}
              onChange={(e) => handleColorChange('logoOpacity', parseFloat(e?.target?.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>
      )}

      {/* Reset Button */}
      <div className="pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={() => {
            onStylingChange({
              foregroundColor: '#000000',
              backgroundColor: '#ffffff',
              logoUrl: null,
              logoSize: 20,
              errorCorrectionLevel: 'M',
              size: 256,
              logoMargin: 8,
              hideBackgroundDots: true,
              excavate: true,
              roundedCorners: false,
              // Reset advanced options
              embeddingStyle: 'overlay',
              embedText: '',
              textSize: 24,
              textWeight: 'bold',
              textColor: null,
              dotStyle: 'square',
              cornerStyle: 'square',
              logoBorderRadius: 0,
              logoOpacity: 1.0
            });
            setLogoError(null);
            setLogoSuccess(null);
          }}
          className="text-sm text-gray-600 hover:text-gray-800 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Reset to Defaults
        </button>
      </div>
    </div>
    );
  };

  return (
    <div className="bg-white">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">QR Code Styling</h3>
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-4">
        {tabs?.map((tab) => {
          const IconComponent = tab?.icon;
          return (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <IconComponent className="w-4 h-4" />
              <span className="hidden sm:inline">{tab?.label}</span>
            </button>
          );
        })}
      </div>
      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'colors' && renderColorsTab()}
        {activeTab === 'logo' && renderLogoTab()}
        {activeTab === 'advanced' && renderAdvancedTab()}
      </div>
    </div>
  );
}

export default QRStylingPanel;